import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/api/client";
import { Loader2, ShieldAlert } from "lucide-react";

interface RequiredRoleProps {
  children: ReactNode;
  roles: string[];
  site?: string;
}

interface SsoSessionInfo {
  valid: boolean;
  user_id: string;
  email: string;
  full_name: string;
  roles: string[];
  detail?: {
    required_roles?: string[];
    your_roles?: string[];
    message?: string;
  };
}

/**
 * FOFUS ID role gate (Workstream 2 / SSO-AUTH-PLAN P1).
 *
 * Wraps routes that need a specific role on this site. Flow:
 *  1. If already signed in locally and role matches → render children.
 *  2. Otherwise attempt silent SSO: GET /auth/sso/session with credentials
 *     (the HttpOnly foid_session cookie on .fofus.in rides along). If the
 *     session exists and carries a required role → auto-signed-in.
 *  3. Signed in but wrong role → 403 "Request access" page (never a blank screen).
 *  4. No session anywhere → prompt to sign in on designai (FOFUS ID home).
 *
 * White-blue UI only (FOFUS UX law).
 */
export default function RequiredRole({ children, roles, site = "this site" }: RequiredRoleProps) {
  const [state, setState] = useState<"checking" | "granted" | "denied" | "anonymous">("checking");
  const [session, setSession] = useState<SsoSessionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Already have a local token? Use /auth/me roles via /sso/session fallback.
    const localToken =
      localStorage.getItem("fofus_access_token") || localStorage.getItem("fofus_guest_token");

    const check = async () => {
      // 1. Local token present? Validate it first (cookie probe alone can 401
      //    when the foid_session cookie didn't survive a cross-site flow).
      if (localToken) {
        try {
          const me = await apiClient.get("/auth/me");
          if (cancelled) return;
          const meRoles: string[] = (me.data as { roles?: string[] })?.roles ||
            ((me.data as { role?: string })?.role ? [(me.data as { role: string }).role] : ["customer"]);
          if (meRoles.some((r) => roles.includes(r))) {
            setState("granted");
            return;
          }
          setSession({
            valid: true,
            user_id: (me.data as { id?: string })?.id || "",
            email: (me.data as { email?: string })?.email || "",
            full_name: (me.data as { full_name?: string })?.full_name || "",
            roles: meRoles,
          });
          setState("denied");
          return;
        } catch {
          /* fall through to cookie probe */
        }
      }
      try {
        const res = await apiClient.get("/auth/sso/session", { withCredentials: true });
        if (cancelled) return;
        const info = res.data as SsoSessionInfo;
        setSession(info);
        const userRoles: string[] = info.roles || ["customer"];
        if (userRoles.some((r) => roles.includes(r))) {
          // Persist token locally if we were only silently authenticated
          if (!localToken) {
            try {
              const cookieRes = await apiClient.post("/auth/sso/cookie", {}, { withCredentials: true });
              if (cookieRes.data?.token) {
                localStorage.setItem("fofus_access_token", cookieRes.data.token);
              }
            } catch {
              /* silent best-effort */
            }
          }
          setState("granted");
        } else {
          setState("denied");
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          setSession((err as { response?: { data?: SsoSessionInfo } })?.response?.data ?? null);
          setState("denied");
        } else {
          setState("anonymous");
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === "checking") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (state === "granted") return <>{children}</>;

  const yourRoles = (session as { detail?: { your_roles?: string[] } } | null)?.detail?.your_roles ||
    session?.roles || [];
  const requiredRoles = (session as { detail?: { required_roles?: string[] } } | null)?.detail
    ?.required_roles || roles;

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-white px-4">
      <div className="w-full max-w-md rounded-2xl border border-blue-100 bg-white p-8 text-center shadow-lg shadow-blue-50">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
          <ShieldAlert className="h-7 w-7 text-blue-600" />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-slate-900">
          {state === "denied" ? "Access restricted" : "Sign in with FOFUS ID"}
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-slate-600">
          {state === "denied" ? (
            session?.detail?.message || "You don't have access to this site."
          ) : (
            <>Sign in with your FOFUS ID to access {site}.</>
          )}
        </p>

        {state === "denied" && (yourRoles.length > 0 || requiredRoles.length > 0) ? (
          <div className="mb-6 rounded-lg bg-blue-50 p-3 text-xs text-slate-600">
            <p>
              Signed in as <span className="font-medium text-slate-800">{session?.email}</span>
            </p>
            <p className="mt-1">
              Your roles: <span className="font-mono">{yourRoles.join(", ") || "customer"}</span>
              {" · "}Required: <span className="font-mono">{requiredRoles.join(" or ")}</span>
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {state === "anonymous" ? (
            <Link
              to="/login"
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Sign in with FOFUS ID
            </Link>
          ) : (
            <a
              href="https://fofus.in/request-access"
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Request access
            </a>
          )}
          <a
            href="https://designai.fofus.in"
            className="w-full rounded-lg border border-blue-200 px-4 py-2.5 text-sm font-medium text-blue-700 transition hover:bg-blue-50"
          >
            Back to FOFUS
          </a>
        </div>
      </div>
    </div>
  );
}